import { z } from 'zod';
import { IdSchema } from './encoding.js';
/**
 * Organization-domain DTOs (ERD §4.2; FRD-CORE-002, CORE-004, CORE-005, CORE-007).
 *
 * Every master carries a business identifier (`code`, unique and never reused) and an
 * Active / Inactive status. Codes are supplied by the business: FRD-CORE-035 lists the identifiers
 * the system generates, and none of these masters is among them.
 */
const CodeSchema = z
    .string()
    .regex(/^[A-Z][A-Z0-9_-]{0,19}$/, 'must be upper case, starting with a letter, up to 20 characters');
const NameSchema = z.string().min(1).max(100);
const MasterStatusSchema = z.enum(['active', 'inactive']);
export const CreateOrganizationRequestSchema = z.strictObject({
    code: CodeSchema,
    legalName: z.string().min(1).max(200),
    registeredAddressLine1: z.string().min(1).max(200),
    registeredAddressLine2: z.string().max(200).optional(),
    registeredCity: z.string().min(1).max(100),
    registeredCountry: z.string().min(1).max(100),
});
export const UpdateOrganizationRequestSchema = z.strictObject({
    legalName: z.string().min(1).max(200).optional(),
    registeredAddressLine1: z.string().min(1).max(200).optional(),
    registeredAddressLine2: z.string().max(200).optional(),
    registeredCity: z.string().min(1).max(100).optional(),
    registeredCountry: z.string().min(1).max(100).optional(),
});
export const OrganizationSchema = z.object({
    id: IdSchema,
    code: z.string(),
    legalName: z.string(),
    registeredAddressLine1: z.string(),
    registeredAddressLine2: z.string().nullable(),
    registeredCity: z.string(),
    registeredCountry: z.string(),
    status: MasterStatusSchema,
    /** For `If-Match` on a change (SDD §7.3). */
    version: z.number().int(),
});
export const CreateDepartmentRequestSchema = z.strictObject({
    code: CodeSchema,
    name: NameSchema,
    /** The department this one sits under (ERD §4.2). Absent means a root department. */
    parentDepartmentId: IdSchema.optional(),
});
export const UpdateDepartmentRequestSchema = z.strictObject({
    name: NameSchema.optional(),
    /** `null` detaches the department, making it a root. */
    parentDepartmentId: IdSchema.nullable().optional(),
    status: MasterStatusSchema.optional(),
});
export const DepartmentSchema = z.object({
    id: IdSchema,
    code: z.string(),
    name: z.string(),
    parentDepartmentId: IdSchema.nullable(),
    status: MasterStatusSchema,
    version: z.number().int(),
});
/** Cost Center and Location share a shape, not an implementation: each is its own master. */
export const CreateCostCenterRequestSchema = z.strictObject({
    code: CodeSchema,
    name: NameSchema,
});
export const UpdateCostCenterRequestSchema = z.strictObject({
    name: NameSchema.optional(),
    status: MasterStatusSchema.optional(),
});
export const CreateLocationRequestSchema = z.strictObject({
    code: CodeSchema,
    name: NameSchema,
});
export const UpdateLocationRequestSchema = z.strictObject({
    name: NameSchema.optional(),
    status: MasterStatusSchema.optional(),
});
export const ReferenceMasterSchema = z.object({
    id: IdSchema,
    code: z.string(),
    name: z.string(),
    status: MasterStatusSchema,
    version: z.number().int(),
});
/**
 * Product / Service (ERD §4.3): the minimum the sources support — an identifier supplied by the
 * business, a name, a description and the Active / Inactive status. No Release 1 record references
 * it; its owning module is open (DM-01).
 */
export const ProductServiceSchema = z.object({
    id: IdSchema,
    code: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    status: MasterStatusSchema,
    version: z.number().int(),
});
export const CreateProductServiceRequestSchema = z.strictObject({
    code: CodeSchema,
    name: z.string().trim().min(1).max(200),
    description: z.string().trim().min(1).max(4000).optional(),
});
/** A partial change; `null` clears the description. The code never changes. */
export const UpdateProductServiceRequestSchema = z.strictObject({
    name: z.string().trim().min(1).max(200).optional(),
    description: z.string().trim().min(1).max(4000).nullable().optional(),
    status: MasterStatusSchema.optional(),
});
//# sourceMappingURL=organization.js.map